import React from "react";
import { useParams } from "react-router-dom";
import "./ServicesPage.css";
import { useLocalContext } from "../../context/LocalContext";

const ServicesPage = () => {
  const { serviceId } = useParams();
  const { webinfo = {} } = useLocalContext();

  const brandName = webinfo.name || "Our team";

  // Updated content (same IDs so your routing keeps working)
  const serviceData = [
  {
    id: "go-to-market-strategy",
    title: "Go-to-Market & Funnel Strategy",
    description:
      "Clarify your offer, audience, and funnel. We map the journey from first touch to closed deal so you know exactly what to run and why.",
    details:
      "Most teams are running channels without a clear Go-to-Market spine. We start by understanding your offer, ideal customers, and current funnel. Then we design a GTM plan that ties positioning, messaging, channels, and measurement together. You get a practical, channel-agnostic strategy and a funnel map that shows how strangers become leads, opportunities, and customers—plus what needs to happen at each stage.",
    features: [
      "Offer and audience clarity: ICP, pain points, and buying triggers.",
      "Positioning and messaging that’s simple enough for sales and marketing to reuse.",
      "End-to-end funnel map from first touch to closed/won and expansion.",
      "Channel and campaign recommendations based on your stage and budget.",
      "Lead qualification, handoff, and follow-up flows between marketing and sales.",
      "KPI tree and dashboards so everyone is looking at the same numbers.",
    ],
    testimonials: [
      {
        name: "Megan O’Donnell",
        feedback:
          "Our GTM finally feels intentional. Everyone can see the funnel, the gaps, and what we’re focusing on this quarter."
      },
      {
        name: "Harsh Vora",
        feedback:
          "They turned scattered experiments into a clear plan. We know which plays to run for awareness, demand, and pipeline."
      },
      {
        name: "Rishi Menon",
        feedback:
          "The funnel map became our internal language. It’s how we plan campaigns, standups, and reporting now."
      }
    ],
    faqs: [
      {
        question: "What do you deliver in a GTM project?",
        answer:
          "A clear positioning and messaging outline, a full-funnel map, channel and campaign recommendations, and a 60–90 day execution plan."
      },
      {
        question: "Is this only for new product launches?",
        answer:
          "No. We also work with existing products that need a sharper narrative, cleaner funnel, or new markets."
      },
      {
        question: "Can you work with our sales leadership?",
        answer:
          "Yes. We usually involve founders, marketing, and sales leaders so the funnel works across the entire revenue team."
      }
    ]
  },

  {
    id: "websites-funnels",
    title: "Websites & Landing Funnels",
    description:
      "Fast, responsive websites and landing pages designed to convert—built on React, Next.js or WordPress.",
    details:
      "Your website and landing pages are where all your traffic is asked to make a decision. We design and build experiences that load fast, explain your value clearly, and make the next step obvious. Whether it’s a full marketing site, a campaign funnel, or a set of product pages, we focus on structure, messaging, and UX patterns that reduce friction and improve conversion rates.",
    features: [
      "Discovery and UX flow mapping for key journeys (home, product, pricing, contact).",
      "High-converting page layouts with clear hierarchy, proof, and CTAs.",
      "Builds on React/Next.js or WordPress with clean, maintainable setups.",
      "Mobile-first, performance-focused implementation with Core Web Vitals in mind.",
      "Form and booking flows integrated with your CRM and email tools.",
      "Analytics, events, and goals wired so you can track performance per page.",
    ],
    testimonials: [
      {
        name: "Emma Richards",
        feedback:
          "The new site finally tells our story in a way that makes sense to visitors—and we can see the impact in demo requests."
      },
      {
        name: "Rahul Gupta",
        feedback:
          "Our landing funnels load fast, look sharp, and are easy to duplicate for new campaigns. Conversion rates improved across paid traffic."
      },
      {
        name: "Sofia Marin",
        feedback:
          "We went from a patchwork website to a proper system of pages and sections our team can reuse without breaking design."
      }
    ],
    faqs: [
      {
        question: "Which platforms do you build on?",
        answer:
          "We primarily work with React/Next.js and modern WordPress setups, but we can adapt to your existing stack if needed."
      },
      {
        question: "Can you handle both design and development?",
        answer:
          "Yes. We can take full ownership from wireframes to live pages, or collaborate with your in-house designers and developers."
      },
      {
        question: "Do you support ongoing changes after launch?",
        answer:
          "We can either hand off with documentation or stay on as a partner to keep shipping new pages, tests, and improvements."
      }
    ]
  },

  {
    id: "paid-growth",
    title: "Paid Growth Engines",
    description:
      "Acquisition systems across Google & Meta with clear targets, testing roadmaps, and ROAS tracking.",
    details:
      "Paid channels work best when they’re treated like a system, not a slot machine. We architect and manage Google and Meta ads with a focus on stable performance: clean account structures, sharp offers, relevant audiences, and rigorous testing. You’ll know exactly what you’re spending, what you’re getting back, and which levers we’re pulling each week.",
    features: [
      "Account and tracking audit to fix conversion tracking and attribution issues.",
      "Campaign structures for search, Performance Max, and paid social with intent-based segmentation.",
      "Testing roadmap for hooks, creatives, landing pages, and audiences.",
      "Clear reporting on spend, ROAS, CPA, and down-funnel metrics like opportunities or revenue.",
      "Budget pacing, bid strategies, and negative lists to control waste.",
      "Regular performance reviews with concrete actions for the next sprint.",
    ],
    testimonials: [
      {
        name: "Nathan Cole",
        feedback:
          "They treated our ad spend like an investment, not a gamble. We cut wasted spend and scaled only the campaigns that proved themselves."
      },
      {
        name: "Mitali Desai",
        feedback:
          "Going from messy accounts to a structured growth engine made a huge difference. Performance is more predictable now."
      },
      {
        name: "Arun Iyer",
        feedback:
          "Their reports are simple but powerful. We know what’s working, what changed, and where to double down."
      }
    ],
    faqs: [
      {
        question: "What platforms do you manage?",
        answer:
          "We primarily work with Google Ads and Meta (Facebook/Instagram). Depending on your audience, we can explore LinkedIn and YouTube as well."
      },
      {
        question: "What kind of budgets do you work with?",
        answer:
          "We’re comfortable with both lean test budgets and higher-scale spends. The key is having room to run meaningful experiments and learn."
      },
      {
        question: "Can you also build the landing pages for campaigns?",
        answer:
          "Yes. We can design and build campaign-specific pages or collaborate with your internal team to get them live quickly."
      }
    ]
  },

  {
    id: "seo-content",
    title: "SEO & Content Systems",
    description:
      "Compounding organic traffic with technical SEO, content hubs, and pages built around real search intent.",
    details:
      "Organic should be more than a blog that nobody reads. We help you turn search into a compounding channel: fixing technical issues, building topic clusters, and creating content that’s designed to rank and convert. Instead of chasing every keyword, we focus on the ones aligned with your buyers and their journey.",
    features: [
      "Technical SEO audit and prioritized fix list for your dev or our team.",
      "Keyword and topic strategy mapped to your offers and funnel stages.",
      "Content hub and cluster planning (pillars, support articles, and landing pages).",
      "On-page optimization for existing and new pages, including metadata and internal links.",
      "Schema and structured data opportunities to improve click-through and visibility.",
      "Monthly reporting on rankings, traffic, leads, and content performance.",
    ],
    testimonials: [
      {
        name: "Anika Verma",
        feedback:
          "Our content finally has a purpose. We rank for meaningful keywords and organic leads keep climbing month over month."
      },
      {
        name: "Devansh Mehta",
        feedback:
          "They helped us clean up technical debt and turn scattered blogs into focused content hubs that drive pipeline."
      },
      {
        name: "Lisa Grant",
        feedback:
          "We understand SEO strategy now instead of just staring at keyword lists. Their explanations and reports are super clear."
      }
    ],
    faqs: [
      {
        question: "How long does SEO usually take?",
        answer:
          "Expect early movement in 8–12 weeks and more material, compounding impact over 3–6 months, depending on your competition and current authority."
      },
      {
        question: "Do you write the content or only plan it?",
        answer:
          "We can do both. We can deliver detailed briefs for your writers or manage the content production end-to-end with our partners."
      },
      {
        question: "Can you work with our dev team on technical fixes?",
        answer:
          "Yes. We provide clear tickets with context and expected impact so your developers know exactly what to implement."
      }
    ]
  },

  {
    id: "automation-crm",
    title: "Automation & CRM Flows",
    description:
      "Make every lead count. We plug your forms, ads, and site into CRM, email, and reporting dashboards.",
    details:
      "Leads are too expensive to lose in spreadsheets or inboxes. We connect your website, ads, and tools to your CRM and marketing automation so every contact is captured, enriched, and followed up with. From simple autoresponders to multi-step nurture journeys, we design flows that support your sales process instead of fighting it.",
    features: [
      "Audit of current lead capture, routing, and follow-up process.",
      "Integration of forms, chat, and ad platforms with your CRM.",
      "Lead qualification rules and routing to the right owner or team.",
      "Email and nurture sequences aligned to your funnel stages.",
      "Deal pipelines, tasks, and notifications so nothing slips through.",
      "Reporting dashboards for leads, stages, and revenue attribution.",
    ],
    testimonials: [
      {
        name: "Sanjana Rao",
        feedback:
          "We stopped losing leads between forms, inbox, and CRM. Everything is tracked and routed with clear next steps."
      },
      {
        name: "Mark Hughes",
        feedback:
          "Sales and marketing finally see the same data. Follow-up is faster, and our pipeline view is way more accurate."
      },
      {
        name: "Ishaan Patel",
        feedback:
          "Their automation work didn’t feel overbuilt. It’s just enough structure to support the team and can grow with us."
      }
    ],
    faqs: [
      {
        question: "Which CRMs and tools do you work with?",
        answer:
          "We commonly work with HubSpot, Pipedrive, Zoho, and similar tools, plus popular form, chat, and email platforms."
      },
      {
        question: "Can you fix our existing CRM setup?",
        answer:
          "Yes. We can clean up fields, pipelines, automations, and reports instead of starting from scratch, if that’s the better option."
      },
      {
        question: "Will you also train our team?",
        answer:
          "We usually include short Looms or live walkthroughs so your team knows how to use the new flows and dashboards."
      }
    ]
  },

  {
    id: "growth-partners",
    title: "Ongoing Growth Partnership",
    description:
      "A cross-functional pod plugged into your team—owning experiments, reporting, and continuous improvements.",
    details:
      "If you’d rather have one accountable partner than five disconnected vendors, this is for you. We plug in as an extended growth team: planning sprints, running experiments across site, SEO, paid, and CRM, and reporting on what’s moving the needle. You get a calm, consistent operating rhythm and a clear view of how marketing is driving pipeline.",
    features: [
      "Quarterly planning with clear goals, bets, and focus areas.",
      "Bi-weekly or monthly sprints with prioritized experiments and tasks.",
      "Cross-channel support across web, SEO, paid, and automation as needed.",
      "Central reporting so leadership sees one unified view of performance.",
      "Async updates and shared docs so everyone stays on the same page.",
      "A steady partner who knows your context and can spot patterns over time.",
    ],
    testimonials: [
      {
        name: "Ben Turner",
        feedback:
          "Having one pod own the growth stack is a game changer. Less coordination overhead, more actual execution."
      },
      {
        name: "Jasper Lin",
        feedback:
          "They feel like part of our team. The cadence, reporting, and experimentation culture have leveled us up."
      },
      {
        name: "Priya Shah",
        feedback:
          "Instead of random tasks, we now run focused sprints tied to clear outcomes. That’s made the whole team calmer and more effective."
      }
    ],
    faqs: [
      {
        question: "How does the partnership usually work?",
        answer:
          "We agree on goals and a monthly scope, then run structured sprints with a shared backlog, check-ins, and transparent reporting."
      },
      {
        question: "What’s the minimum commitment?",
        answer:
          "Most partnerships work best with a 3–6 month horizon, but we can start with a shorter initial phase to prove value."
      },
      {
        question: "Do you work alongside existing agencies or teams?",
        answer:
          "Yes. We can either be your primary growth partner or slot in alongside in-house teams and other vendors to coordinate efforts."
      }
    ]
  }
];

  const service = serviceData.find((s) => s.id === serviceId);

  if (!service) {
    return (
      <div className="service-details-container">
        <header className="service-header">
          <p className="service-eyebrow">Services</p>
          <h1>Service not found</h1>
          <p className="service-description">
            We couldn’t find that service. Please go back to the Services page and choose an option.
          </p>
        </header>
      </div>
    );
  }

  return (
    <div className="service-details-container">
      {/* Header */}
      <header className="service-header" aria-labelledby="service-title">
        <p className="service-eyebrow">What we do</p>
        <h1 id="service-title">{service.title}</h1>
        <p className="service-description">{service.description}</p>
      </header>

      {/* Main layout */}
      <div className="service-main">
        <div className="service-content" role="region" aria-label="Service content">
          <section className="section-card" aria-labelledby="overview-heading">
            <h2 id="overview-heading">How {brandName} helps</h2>
            <p>{service.details}</p>
          </section>

          <section className="section-card" aria-labelledby="features-heading">
            <h2 id="features-heading">What’s included</h2>
            <div className="features-grid">
              {service.features.map((f, i) => (
                <div className="feature-item" key={i}>
                  <span className="tick" aria-hidden="true" />
                  <span>{f}</span>
                </div>
              ))}
            </div>
          </section>

          {service.faqs?.length > 0 && (
            <section className="section-card faqs" aria-labelledby="faqs-heading">
              <h2 id="faqs-heading">Common questions</h2>
              {service.faqs.map((qa, i) => (
                <details key={i}>
                  <summary>{qa.question}</summary>
                  <p>{qa.answer}</p>
                </details>
              ))}
            </section>
          )}
        </div>

        {/* Sidebar */}
        <aside className="sidebar" aria-label="Contact and meta">
          <div className="sidebar-card">
            <p className="sidebar-eyebrow">Ready to talk?</p>
            <h3>Book a quick call</h3>
            <p className="sidebar-copy">
              Share where you are today and what you want to improve. We’ll walk through options and next steps—no hard sell.
            </p>

            <div className="meta">
              <div>
                <strong>Call:</strong>{" "}
                <a href={`tel:${webinfo.phonecall || ""}`}>
                  {webinfo.phone || "+91 80000 00000"}
                </a>
              </div>
              <div>
                <strong>Email:</strong>{" "}
                <a href={`mailto:${webinfo.email || "hello@example.com"}`}>
                  {webinfo.email || "hello@example.com"}
                </a>
              </div>
              <div>
                <strong>Hours:</strong>{" "}
                {webinfo.hours || "Mon–Sat · 10:00–19:00 IST"}
              </div>
            </div>

            <div className="pill-row">
              <span className="pill">No long contracts</span>
              <span className="pill">Weekly updates</span>
              <span className="pill">Transparent metrics</span>
            </div>

            <button
              className="cta-button"
              onClick={() => (window.location.href = `tel:${webinfo.phonecall || ""}`)}
            >
              Call us: {webinfo.phone || "+91 80000 00000"}
            </button>
          </div>
        </aside>
      </div>

      {/* Testimonials */}
      {service.testimonials?.length ? (
        <section className="testimonials-section" aria-labelledby="testimonials-heading">
          <h2 id="testimonials-heading">What clients say</h2>
          <div className="testimonial-grid">
            {service.testimonials.map((t, i) => (
              <div className="testimonial-box" key={i}>
                <p>“{t.feedback}”</p>
                <span>— {t.name}</span>
              </div>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
};

export default ServicesPage;
